import ApiVideoPlayerBase from "@api.video/react-player"
import playerjs, { Player } from "player.js"
import { type FC, useEffect, useId, useRef } from "react"

import { cn } from "../utilities/tailwind"
import AspectRatio from "./AspectRatio"

async function getDuration(player: Player, abortSignal: AbortSignal): Promise<number> {
	await new Promise(resolve => setTimeout(resolve, 500))

	if (abortSignal.aborted) return 0

	return new Promise(resolve => {
		player.getDuration(duration => {
			if (abortSignal.aborted) return resolve(0)
			if (duration === 0) {
				getDuration(player, abortSignal).then(resolve)
			} else {
				resolve(duration)
			}
		})
	})
}

type BunnyPlayerProps = { videoId: string; onTrackWatch?(progress: number): void; className?: string }
export const BunnyPlayer: FC<BunnyPlayerProps> = ({ videoId, onTrackWatch, className }) => {
	const iframeId = useId()
	const onTrackWatchRef = useRef(onTrackWatch)
	onTrackWatchRef.current = onTrackWatch
	const isTracking = onTrackWatch !== undefined

	// Callers pass an inline onTrackWatch, so it's read through a ref to avoid creating a Player per render
	useEffect(() => {
		if (!isTracking) return

		const iframe = document.getElementById(iframeId)
		if (!iframe) return

		const player = new playerjs.Player(iframe)
		let lastTrackedProgress: number | null = null
		let duration = 0

		const abortController = new AbortController()

		getDuration(player, abortController.signal).then(d => {
			if (abortController.signal.aborted) return
			duration = d
		})

		player.on("timeupdate", ({ seconds }) => {
			if (abortController.signal.aborted) return
			if (duration === 0) return
			const progress = seconds / duration
			if (lastTrackedProgress === null || progress > lastTrackedProgress + 0.05) {
				lastTrackedProgress = progress
				onTrackWatchRef.current?.(progress)
			}
		})

		player.on("ended", () => {
			if (abortController.signal.aborted) return
			onTrackWatchRef.current?.(1)
		})

		return () => {
			abortController.abort()
			// player.js has no destroy and its window message listener can't be removed, so stop it from handling
			// messages that would otherwise post to this iframe after it's detached
			player.receive = () => false
		}
	}, [isTracking, iframeId, videoId])

	return (
		<AspectRatio width={{ unit: "percent", value: 100 }} aspectRatio={16 / 9} className={className}>
			<iframe
				id={iframeId}
				src={`/api/bunny/iframe/${videoId}`}
				allowFullScreen
				allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
				className="h-full w-full border-none"
			/>
		</AspectRatio>
	)
}

type ApiVideoPlayerProps = { apiVideoId: string; className?: string }
export const ApiVideoPlayer: FC<ApiVideoPlayerProps> = ({ apiVideoId, className }) => (
	<div className={cn("aspect-video w-full", className)}>
		<ApiVideoPlayerBase
			video={{ id: apiVideoId }}
			hideTitle
			controls={[
				"chromecast",
				"fullscreen",
				"pictureInPicture",
				"play",
				"playbackRate",
				"progressBar",
				"seekBackward",
				"seekForward",
				"volume",
			]}
			style={{ width: "100%", height: "100%" }}
		/>
	</div>
)

type YoutubePlayerProps = { videoId: string; autoplay?: boolean; className?: string }
export const YoutubePlayer: FC<YoutubePlayerProps> = ({ videoId, autoplay, className }) => (
	<iframe
		src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}`}
		allowFullScreen
		allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
		referrerPolicy="strict-origin-when-cross-origin"
		className={cn("aspect-video w-full border-none", className)}
	/>
)

type VimeoPlayerProps = { videoId: string; autoplay?: boolean; className?: string }
export const VimeoPlayer: FC<VimeoPlayerProps> = ({ videoId, autoplay, className }) => (
	<iframe
		src={`https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0&badge=0&autopause=0&autoplay=${autoplay ? 1 : 0}`}
		allowFullScreen
		allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
		referrerPolicy="strict-origin-when-cross-origin"
		className={cn("aspect-video w-full border-none", className)}
	/>
)

export const LoomPlayer: FC<{ videoId: string; className?: string }> = ({ videoId, className }) => (
	<iframe
		src={`https://www.loom.com/embed/${videoId}?hideEmbedTopBar=true&hide_title=true&hide_owner=true`}
		allowFullScreen
		className={cn("aspect-video w-full border-none", className)}
	/>
)
